import { Box } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  checkLivePreviewEligibility,
  createPreviewGallery,
  fetchPreviewContext,
  generateLivePreview,
  generateSimulatedPreview,
} from '../../api/ventura/preview';
import { useConfig } from '../../hooks/useConfig';
import { useSnackBar } from '../../hooks/useSnackBar';
import {
  DeviceOS,
  DeviceType,
  LivePreviewDiagnostics,
  LivePreviewEligibility,
  PreviewContext,
  PreviewMode,
  SimulatePreviewRequest,
  SimulatePreviewResponse,
  SourceMode,
  STANDARD_CREATIVE_SIZES,
} from '../../types/previewStudio';
import { detectSizeFromTag, getDeviceTypeFromAdSize } from '../../util/tagDetection';
import { wrapWithClickTag } from '../Creatives/forms/fields/AutoTag/MacroHandler/macroUtils';
import ControlsPanel from './ControlsPanel';
import DebugPanel from './DebugPanel';
import GalleryCreationModal from './GalleryCreationModal';
import PreviewPanel from './PreviewPanel';
import ShareWithClientModal from './ShareWithClientModal';
import TopBar from './TopBar';

// Default size when no valid size can be determined
const DEFAULT_FALLBACK_SIZE = '320x480';

function isValidSize(size: string | null | undefined): boolean {
  if (!size) return false;
  return STANDARD_CREATIVE_SIZES.some(s => `${s.width}x${s.height}` === size);
}

function getValidSizeOrDefault(size: string | null | undefined): string {
  if (isValidSize(size)) return size!;
  return DEFAULT_FALLBACK_SIZE;
}

function isVideoFriendlySize(size: string): boolean {
  const [width, height] = size.split('x').map(Number);
  if (!width || !height) return false;
  const aspectRatio = width / height;
  return Math.abs(aspectRatio - 16/9) < 0.1; // Allow small tolerance
}

/**
 * Generate a basic HTML tag for hosted image creatives that don't have a pre-defined tag.
 * This allows simulated preview to work with hosted images by creating a click-wrapped img tag.
 */
function generateHostedImageTag(imageUrl: string, size: string): string {
  const [width, height] = size.split('x').map(Number);
  // Basic click-wrapped image tag with URL macro for click tracking
  return `<a href="%{URL0}" target="_blank" style="display:block;width:${width}px;height:${height}px;">
  <img src="${imageUrl}" width="${width}" height="${height}" border="0" style="display:block;" />
</a>`;
}

/**
 * Turn a backend preview_url into a client-shareable link, shortened via TinyURL
 * when an API key is configured. Falls back to the full URL if shortening fails.
 * The link is anchored to the same host the preview record was saved on.
 */
async function toShareableUrl(apiHost: string, previewUrl: string): Promise<string> {
  const fullUrl = previewUrl.startsWith('/') ? `${apiHost}${previewUrl}` : previewUrl;

  const tinyUrlApiKey = import.meta.env.VITE_TINYURL_API_KEY;
  if (tinyUrlApiKey) {
    try {
      const tinyResponse = await fetch(
        `https://api.tinyurl.com/create?api_token=${tinyUrlApiKey}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            url: fullUrl,
            domain: 'tiny.inmarket.com',
          }),
        }
      );

      if (tinyResponse.ok) {
        const data = await tinyResponse.json();
        return data.data.tiny_url;
      }
    } catch {
      // TinyURL failed, use full URL
    }
  }

  return fullUrl;
}

interface PreviewStudioLayoutProps {
  initialContext: PreviewContext | null;
  initialRawTag?: string;
}

// Map error payloads to normalized error codes
function mapEligibilityCode(payload: any): string {
  if (payload?.eligibility && payload.eligibility.eligible === false) {
    return 'LIVE_PREVIEW_NOT_ELIGIBLE';
  }
  if (payload?.errors?.some((e: string) => e.includes('Malibu'))) {
    return 'MALIBU_NOT_PUBLISHED';
  }
  return 'LIVE_PREVIEW_FAILED';
}

export default function PreviewStudioLayout({ initialContext, initialRawTag }: PreviewStudioLayoutProps) {
  const { apiHost } = useConfig();
  const { showSnackBar } = useSnackBar();
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize preview mode from URL
  const modeParam = searchParams.get('mode');
  const initialMode: PreviewMode =
    modeParam === 'live' || modeParam === 'simulated' ? modeParam : 'simulated';

  // Core state
  const [context, setContext] = useState<PreviewContext | null>(initialContext);
  const [sourceMode, setSourceModeState] = useState<SourceMode>(initialRawTag ? 'raw_tag' : 'campaign');
  const [previewMode, setPreviewMode] = useState<PreviewMode>(initialMode);
  const [previewResponse, setPreviewResponse] = useState<SimulatePreviewResponse | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [lastGeneratedTime, setLastGeneratedTime] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Live Preview state
  const [liveEligibility, setLiveEligibility] = useState<LivePreviewEligibility | null>(null);
  const [checkingEligibility, setCheckingEligibility] = useState(false);
  const [eligibilityError, setEligibilityError] = useState<string | null>(null);
  const [fireTracking, setFireTracking] = useState(false);
  const [liveDiagnostics, setLiveDiagnostics] = useState<LivePreviewDiagnostics | null>(null);

  // Image upload for click tag generation
  const [uploadedImageUrl, setUploadedImageUrl] = useState<string | null>(null);
  const [generateClickTag, setGenerateClickTag] = useState(false);

  // Share with client modal state
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [isSharing, setIsSharing] = useState(false);
  const [shareError, setShareError] = useState<string | null>(null);

  // Gallery creation modal state
  const [galleryModalOpen, setGalleryModalOpen] = useState(false);

  // Initialize from URL params for creative-only mode
  const creativeIdParam = searchParams.get('creative_id');
  const campaignIdParam = searchParams.get('campaign_id');

  // Campaign/Creative selection
  const [selectedCampaignId, setSelectedCampaignId] = useState<number | null>(
    initialContext?.context.campaign?.id || (campaignIdParam ? parseInt(campaignIdParam, 10) : null)
  );
  const [selectedCreativeId, setSelectedCreativeId] = useState<number | null>(
    initialContext?.context.creative?.id || (creativeIdParam ? parseInt(creativeIdParam, 10) : null)
  );

  const isCreativeOnly = sourceMode === 'campaign' && !selectedCampaignId && !!selectedCreativeId;

  // Location settings - QA defaults for creative-only mode
  const [locationType, setLocationType] = useState<'default' | 'custom'>(
    (!initialContext && !campaignIdParam && !!creativeIdParam) ? 'custom' : 'default'
  );
  const [selectedLocationId, setSelectedLocationId] = useState<number | null>(
    initialContext?.context.locations?.[0]?.id || null
  );
  const [customLat, setCustomLat] = useState<string>(
    (!initialContext && !campaignIdParam && !!creativeIdParam) ? '34.010977' : ''
  );
  const [customLon, setCustomLon] = useState<string>(
    (!initialContext && !campaignIdParam && !!creativeIdParam) ? '-118.387985' : ''
  );

  // Device settings
  const [deviceType, setDeviceType] = useState<DeviceType>('mobile');
  const [deviceOS, setDeviceOS] = useState<DeviceOS>('ios');
  const getDefaultSize = (): string => {
    if (initialContext?.context.creative?.is_video) {
      return '320x180';
    }
    if (initialContext?.context.targeted_creatives?.some(tc => tc.creative.is_video)) {
      return '320x180';
    }
    const initialSize = initialContext?.context.creative?.size;
    if (isValidSize(initialSize)) {
      return initialSize!;
    }
    return DEFAULT_FALLBACK_SIZE;
  };
  const [selectedSize, setSelectedSize] = useState<string>(getDefaultSize());
  const [expandableMode, setExpandableMode] = useState<boolean>(false);

  // Raw tag mode
  const [rawTag, setRawTag] = useState<string>(initialRawTag || '');

  // Manual URL overrides (for raw tag testing)
  const [campaignUrls, setCampaignUrls] = useState<string[]>([]);
  const [campaignUrlInput, setCampaignUrlInput] = useState<string>('');
  const [pixelUrls, setPixelUrls] = useState<string[]>([]);
  const [pixelUrlInput, setPixelUrlInput] = useState<string>('');

  // Auto-refresh
  const [autoRefresh, setAutoRefresh] = useState(true);

  // Iframe ref for reloading
  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  const handleModeChange = useCallback(
    (newMode: PreviewMode) => {
      setPreviewMode(newMode);
      setPreviewResponse(null);
      setLiveDiagnostics(null);
      setError(null);
      const newParams = new URLSearchParams(searchParams);
      newParams.set('mode', newMode);
      setSearchParams(newParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const handleSourceModeChange = useCallback((newMode: SourceMode) => {
    if (newMode === 'raw_tag' && previewMode === 'live') {
      handleModeChange('simulated');
    }
    setSourceModeState(newMode);
  }, [previewMode, handleModeChange]);

  const handleCheckEligibility = useCallback(async () => {
    if (!selectedCampaignId || !selectedCreativeId) return;

    try {
      setCheckingEligibility(true);
      setEligibilityError(null);
      const eligibility = await checkLivePreviewEligibility(
        apiHost,
        selectedCampaignId,
        selectedCreativeId
      );
      setLiveEligibility(eligibility);
      setEligibilityError(null);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to check eligibility';
      setEligibilityError(errorMessage);
    } finally {
      setCheckingEligibility(false);
    }
  }, [apiHost, selectedCampaignId, selectedCreativeId]);

  useEffect(() => {
    if (previewMode === 'live' && selectedCampaignId && selectedCreativeId) {
      handleCheckEligibility();
    }
  }, [previewMode, selectedCampaignId, selectedCreativeId, handleCheckEligibility]);

  const buildPreviewRequest = useCallback((): SimulatePreviewRequest => {
    const request: SimulatePreviewRequest = {
      device_type: deviceType,
      os: deviceOS,
    };

    if (sourceMode === 'raw_tag') {
      let processedTag = rawTag;

      // Replace mraid.js with mock
      processedTag = processedTag.replace(
        /<script\s+src=["']mraid\.js["']\s*><\/script>/gi,
        '<!-- mraid.js replaced with mock MRAID API (injected in preview iframe) -->'
      );

      const allCampaignUrls = [...campaignUrls];
      const trimmedCampaignInput = campaignUrlInput.trim();
      if (trimmedCampaignInput && 
          (trimmedCampaignInput.startsWith('http://') || trimmedCampaignInput.startsWith('https://')) &&
          !allCampaignUrls.includes(trimmedCampaignInput)) {
        allCampaignUrls.push(trimmedCampaignInput);
      }
      const urlsToUse = allCampaignUrls.length > 0 
        ? allCampaignUrls 
        : ['https://inmarket.com/'];
      
      const allPixelUrls = [...pixelUrls];
      const trimmedPixelInput = pixelUrlInput.trim();
      if (trimmedPixelInput && 
          (trimmedPixelInput.startsWith('http://') || trimmedPixelInput.startsWith('https://')) &&
          !allPixelUrls.includes(trimmedPixelInput)) {
        allPixelUrls.push(trimmedPixelInput);
      }
      const pixelsToUse = allPixelUrls;

      // URI encode (matches Ruby's URI::encode)
      const uriEncode = (str: string): string => encodeURIComponent(str)
          .replace(/[!'()*]/g, (c) => `%${  c.charCodeAt(0).toString(16).toUpperCase()}`);

      // Replace URL macros (matches backend VariableReplacer logic)
      urlsToUse.forEach((url, index) => {
        const encodedUrl = uriEncode(url);
        
        // Unescaped variants (U prefix)
        processedTag = processedTag.replace(new RegExp(`%\\{UURL${index}\\}`, 'g'), url);
        processedTag = processedTag.replace(new RegExp(`%\\{UtURL${index}\\}`, 'g'), url);
        processedTag = processedTag.replace(new RegExp(`%\\{UcURL${index}\\}`, 'g'), url);
        
        // href attributes need unencoded URLs
        const hrefPattern = new RegExp(`(href=["'])%\\{URL${index}\\}(["'])`, 'gi');
        processedTag = processedTag.replace(hrefPattern, `$1${url}$2`);

        // Encoded variants
        processedTag = processedTag.replace(new RegExp(`%\\{URL${index}\\}`, 'g'), encodedUrl);
        processedTag = processedTag.replace(new RegExp(`%\\{tURL${index}\\}`, 'g'), encodedUrl);
        processedTag = processedTag.replace(new RegExp(`%\\{cURL${index}\\}`, 'g'), encodedUrl);
        processedTag = processedTag.replace(new RegExp(`%\\{eURL${index}\\}`, 'g'), encodedUrl);
        processedTag = processedTag.replace(new RegExp(`%\\{etURL${index}\\}`, 'g'), encodedUrl);
        processedTag = processedTag.replace(new RegExp(`%\\{ecURL${index}\\}`, 'g'), encodedUrl);
      });

      // Replace pixel URL macros (matches backend VariableReplacer logic)
      pixelsToUse.forEach((url, index) => {
        const encodedUrl = uriEncode(url);
        
        processedTag = processedTag.replace(new RegExp(`%\\{UpURL${index}\\}`, 'g'), url);
        processedTag = processedTag.replace(new RegExp(`%\\{pURL${index}\\}`, 'g'), encodedUrl);
        processedTag = processedTag.replace(new RegExp(`%\\{epURL${index}\\}`, 'g'), encodedUrl);
      });

      // Replace Venice dummy variables (matches VENICE_DUMMY_VARIABLES hash in VariableReplacer)
      // Note: These are OpenRTB macro strings, not template literals
      /* eslint-disable no-template-curly-in-string */
      const veniceDummyVariables: Record<string, string> = {
        '%{TIMESTAMP}': '1',
        '%{DISTANCE}': '1',
        '%{DISTANCE_LOWER}': '1',
        '%{DISTANCE_ALL_CAPS}': '1',
        '%{DISTANCE_LEADING}': '1',
        '%{KM_DISTANCE}': '1',
        '%{KM_DISTANCE_LOWER}': '1',
        '%{KM_DISTANCE_ALL_CAPS}': '1',
        '%{KM_DISTANCE_LEADING}': '1',
        '[timestamp]': '1',
        '%{TOKEN}': '1',
        '${AUCTION_PRICE}': '0.1',
        '${AUCTION_ID}': '1',
        '${AUCTION_AD_ID}': '1',
        '${AUCTION_IMP_ID}': '1',
        '${AUCTION_BID_ID}': '1',
        '${GDPR}': '0',
        '${GDPR_CONSENT_755}': '0',
        '${GDPR_CONSENT_278}': '0',
        '${GDPR_PD}': '0',
        '${ADDTL_CONSENT}': '0',
      };
      /* eslint-enable no-template-curly-in-string */

      Object.entries(veniceDummyVariables).forEach(([macro, value]) => {
        const escapedMacro = macro.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        processedTag = processedTag.replace(new RegExp(escapedMacro, 'g'), value);
      });

      if (generateClickTag && uploadedImageUrl && sourceMode === 'raw_tag') {
        processedTag = wrapWithClickTag(processedTag, uploadedImageUrl, selectedSize);
      }

      request.tag_html = processedTag;
      request.lat = customLat ? parseFloat(customLat) : 34.010977;
      request.lon = customLon ? parseFloat(customLon) : -118.387985;
    } else {
      request.creative_id = selectedCreativeId!;

      if (selectedCampaignId) {
        request.campaign_id = selectedCampaignId;
      }

      // Check if creative is a hosted image (has link field AND ad_type is 'image')
      // Only generate a basic HTML tag for hosted image creatives that don't have a pre-defined tag
      // Tag-based creatives (Celtra, etc.) may also have a link for preview thumbnails but should use their stored tag
      const selectedCreative = context?.context.targeted_creatives?.find(
        (tc) => tc.creative_id === selectedCreativeId
      )?.creative ?? context?.context.creative;

      if (selectedCreative?.link && selectedCreative?.ad_type === 'image') {
        const creativeSize = selectedCreative.size || selectedSize || DEFAULT_FALLBACK_SIZE;
        request.tag_html = generateHostedImageTag(selectedCreative.link, creativeSize);
      }

      let lat: number;
      let lon: number;

      const locations = context?.context.locations ?? [];
      const hasLocations = locations.length > 0;

      if (locationType === 'custom' && customLat && customLon) {
        lat = parseFloat(customLat);
        lon = parseFloat(customLon);
      } else if (hasLocations && locationType === 'default') {
        const loc =
          locations.find((l) => l.id === selectedLocationId) || locations[0];

        if (loc && typeof loc.latitude === 'number' && typeof loc.longitude === 'number') {
          lat = loc.latitude;
          lon = loc.longitude;
        } else {
          lat = customLat ? parseFloat(customLat) : 34.010977;
          lon = customLon ? parseFloat(customLon) : -118.387985;
        }
      } else {
        lat = customLat ? parseFloat(customLat) : 34.010977;
        lon = customLon ? parseFloat(customLon) : -118.387985;
      }

      request.lat = lat;
      request.lon = lon;
    }

    if (sourceMode === 'campaign') {
      if (campaignUrls.length > 0) {
        request.campaign_urls = campaignUrls;
      }
      if (pixelUrls.length > 0) {
        request.pixel_urls = pixelUrls;
      }
    }

    if (selectedSize) {
      request.selected_size = selectedSize;
    }

    return request;
  }, [
    sourceMode,
    deviceType,
    deviceOS,
    rawTag,
    campaignUrls,
    campaignUrlInput,
    pixelUrls,
    pixelUrlInput,
    generateClickTag,
    uploadedImageUrl,
    selectedSize,
    customLat,
    customLon,
    locationType,
    selectedLocationId,
    selectedCampaignId,
    selectedCreativeId,
    context,
  ]);

  const handleShare = useCallback(async (displayName?: string): Promise<string | null> => {
    // generate_live already persists the preview and returns its public route, so
    // reuse that link instead of re-running the simulated flow. previewResponse is
    // cleared on mode change, so anything held here belongs to the current mode.
    if (previewMode === 'live') {
      const livePreviewUrl = previewResponse?.preview_url;
      if (!livePreviewUrl) {
        throw new Error('Generate a live preview before sharing');
      }
      return toShareableUrl(apiHost, livePreviewUrl);
    }

    try {
      const request = buildPreviewRequest();
      request.save_qa_preview = true;
      if (displayName) {
        request.display_name = displayName;
      }

      const creativeOnlyMode = sourceMode === 'campaign' && !selectedCampaignId && !!selectedCreativeId;

      if (sourceMode === 'campaign' && !creativeOnlyMode) {
        if (selectedCampaignId && !request.campaign_id) {
          request.campaign_id = selectedCampaignId;
        }

        if (!request.campaign_id) {
          throw new Error('Campaign ID is required to share preview. Please select a campaign.');
        }
        if (!request.creative_id) {
          throw new Error('Creative ID is required to share preview. Please select a creative.');
        }
      }

      const response = await generateSimulatedPreview(apiHost, request);

      if (response.preview_url) {
        return await toShareableUrl(apiHost, response.preview_url);
      }

      return null;
    } catch (err: any) {
      let errorMessage = err?.message || 'Failed to save preview for sharing';
      try {
        const parsed = JSON.parse(errorMessage);
        if (parsed.errors && Array.isArray(parsed.errors)) {
          errorMessage = parsed.errors.join(', ');
        } else if (parsed.error) {
          errorMessage = parsed.error;
        } else if (parsed.message) {
          errorMessage = parsed.message;
        }
      } catch { /* not JSON */ }

      throw new Error(errorMessage);
    }
  }, [previewMode, previewResponse, buildPreviewRequest, apiHost, selectedCampaignId, selectedCreativeId, sourceMode]);

  const handleShareClick = useCallback(() => {
    setShareModalOpen(true);
    setShareUrl(null);
    setShareError(null);
  }, []);

  const handleGenerateShare = useCallback(async (displayName: string) => {
    setIsSharing(true);
    setShareError(null);

    try {
      const url = await handleShare(displayName || undefined);
      setShareUrl(url);
    } catch (err: any) {
      let errorMessage = err?.message || 'Failed to generate share URL';
      try {
        const parsed = JSON.parse(errorMessage);
        if (parsed.errors && Array.isArray(parsed.errors)) {
          errorMessage = parsed.errors.join(', ');
        } else if (parsed.error) {
          errorMessage = parsed.error;
        } else if (parsed.message) {
          errorMessage = parsed.message;
        }
      } catch { /* not JSON */ }
      setShareError(errorMessage);
    } finally {
      setIsSharing(false);
    }
  }, [handleShare]);

  const handleGalleryClick = useCallback(() => {
    setGalleryModalOpen(true);
  }, []);

  const handleCreateGallery = useCallback(async (params: {
    name: string;
    creative_ids: number[];
    campaign_id: number;
    device_type?: string;
    hosted_image_adms?: Record<number, string>;
  }) => {
    const { hosted_image_adms: hostedImageAdms, ...createParams } = params;
    const result = await createPreviewGallery(apiHost, {
      ...createParams,
      device_type: createParams.device_type || deviceType || 'mobile',
    });

    // Inject hosted image adm into gallery previews that came back without adm
    if (hostedImageAdms && result.previews) {
      result.previews.forEach((preview: any) => {
        if (!preview.adm && hostedImageAdms[preview.creative_id]) {
          // eslint-disable-next-line no-param-reassign
          preview.adm = hostedImageAdms[preview.creative_id];
        }
      });
    }

    // Pre-capture screenshots for all creatives via Lambda.
    // Store results in localStorage keyed by gallery public_route so the gallery
    // page can retrieve them (since the backend doesn't persist screenshot_url yet).
    if (result.previews) {
      // eslint-disable-next-line no-console
      console.log(`[Gallery] Starting screenshot capture for ${result.previews.filter((p: any) => p.adm && p.status === 'ready').length} creatives...`);
      const { captureCreativeScreenshot } = await import('../../util/galleryExport');
      const screenshots: Record<number, string> = {};

      const capturePromises = result.previews
        .filter((p: any) => p.adm && p.status === 'ready')
        .map(async (preview: any) => {
          try {
            const htmlTag = atob(preview.adm);
            const size = `${preview.width || 300}x${preview.height || 250}`;
            // eslint-disable-next-line no-console
            console.log(`[Gallery] Capturing screenshot for creative ${preview.creative_id} (${size})...`);
            const screenshotUrl = await captureCreativeScreenshot(htmlTag, size);
            if (screenshotUrl) {
              // eslint-disable-next-line no-param-reassign
              preview.screenshot_url = screenshotUrl;
              screenshots[preview.creative_id] = screenshotUrl;
              // eslint-disable-next-line no-console
              console.log(`[Gallery] ✓ Screenshot captured for creative ${preview.creative_id}: ${screenshotUrl.substring(0, 60)}...`);
            }
          } catch (e) {
            // eslint-disable-next-line no-console
            console.warn(`[Gallery] ✗ Screenshot failed for creative ${preview.creative_id}:`, e);
          }
        });
      await Promise.allSettled(capturePromises);

      // Persist screenshot URLs so the gallery page can use them
      if (Object.keys(screenshots).length > 0 && result.public_route) {
        try {
          localStorage.setItem(
            `gallery_screenshots_${result.public_route}`,
            JSON.stringify(screenshots)
          );
          // eslint-disable-next-line no-console
          console.log(`[Gallery] Saved ${Object.keys(screenshots).length} screenshots to localStorage`);
        } catch {
          // localStorage full or unavailable
        }
      }
    }

    return result;
  }, [apiHost, deviceType]);

  const handleGeneratePreview = useCallback(async (options?: { silent?: boolean }) => {
    const silent = options?.silent ?? false;
    if (sourceMode === 'campaign') {
      const creativeOnlyState = !selectedCampaignId && !!selectedCreativeId;

      if (!selectedCreativeId) {
        setError('Please select or enter a creative ID. You can also paste a creative ID without a campaign to run in QA mode.');
        return;
      }

      if (!creativeOnlyState && !selectedCampaignId) {
        setError('Please select a campaign or run in creative-only mode');
        return;
      }
    } else if (sourceMode === 'raw_tag') {
      if (!rawTag) {
        setError('Please enter a tag in Creative Sandbox');
        return;
      }
    }

    if (previewMode === 'live') {
      if (sourceMode === 'raw_tag') {
        setError('Live preview is not available for Creative Sandbox mode');
        return;
      }
      if (!selectedCampaignId || !selectedCreativeId) {
        setError('Campaign and Creative are required for live preview');
        return;
      }
    }

    try {
      setIsGenerating(true);
      setError(null);
      if (previewMode === 'live') setLiveDiagnostics(null);

      if (previewMode === 'live') {
        try {
          const response = await generateLivePreview(apiHost, {
            campaign_id: selectedCampaignId!,
            creative_id: selectedCreativeId!,
            location_id: selectedLocationId || undefined,
            fire_tracking: fireTracking,
          });
          setPreviewResponse(response);
          setLastGeneratedTime(new Date());
          setLiveDiagnostics(response.diagnostics || null);
          if (response.diagnostics) {
            setLiveEligibility(null);
          }
          if (!silent) showSnackBar('Live preview generated successfully', 'success', { event: 'preview_live_generated', campaign_id: selectedCampaignId, creative_id: selectedCreativeId });
          return;
        } catch (err: any) {
          let errorResponse = err.response?.data || err.response;

          if (!errorResponse && err.message) {
            try {
              errorResponse = JSON.parse(err.message);
            } catch { /* not JSON */ }
          }

          if (errorResponse?.diagnostics) {
            setLiveDiagnostics(errorResponse.diagnostics);
            setLiveEligibility(null);
            setError(null);
          } else if (errorResponse?.eligibility) {
            // Normalize 'requirements' to 'launch_steps' if needed
            const eligibility = { ...errorResponse.eligibility };
            if (eligibility.requirements && !eligibility.launch_steps) {
              const mapReqStatus = (status: string) => {
                if (status === 'pass') return 'complete';
                if (status === 'error') return 'error';
                return 'warning';
              };
              const mapItemStatus = (status: string) => {
                if (status === 'pass') return 'pass';
                if (status === 'fail') return 'fail';
                return 'warning';
              };
              eligibility.launch_steps = eligibility.requirements.map((req: any, idx: number) => ({
                step: idx + 1,
                title: req.title,
                status: mapReqStatus(req.status),
                checks: req.items?.map((item: any) => ({
                  name: item.name,
                  status: mapItemStatus(item.status),
                  message: item.detail || '',
                })) || [],
              }));
            }
            setLiveEligibility(eligibility);
            setError(null);
          } else {
            const errorMessage = errorResponse?.errors?.join(', ') ||
              (typeof err.message === 'string' && !err.message.startsWith('{') ? err.message : 'Live preview failed');
            setError(errorMessage);
            setLiveDiagnostics(null);
            if (!silent) showSnackBar(errorMessage, 'error', { event: 'preview_live_error', campaign_id: selectedCampaignId, creative_id: selectedCreativeId });
          }
          return;
        }
      }

      const request = buildPreviewRequest();

      if (!silent && sourceMode === 'raw_tag' && request.tag_html) {
        const detectedSize = detectSizeFromTag(request.tag_html);
        if (detectedSize) {
          const validSize = getValidSizeOrDefault(detectedSize);
          setSelectedSize(validSize);
          const suggestedDeviceType = getDeviceTypeFromAdSize(validSize);
          if (suggestedDeviceType) {
            setDeviceType(suggestedDeviceType);
          }
        }
      }

      const response = await generateSimulatedPreview(apiHost, request);
      setPreviewResponse(response);
      setLastGeneratedTime(new Date());

      // Only auto-detect device type on manual generates, not auto-refresh.
      // Auto-refresh should respect the user's explicit device type choice.
      if (!silent && response.metadata?.size) {
        const metadataSize = response.metadata.size;
        if (metadataSize === 'VIDEO_AD' || response.metadata.creative_type === 'video') {
          // Always default video to mobile device
          if (deviceType !== 'mobile') {
            setDeviceType('mobile');
          }
        } else {
          const suggestedDeviceType = getDeviceTypeFromAdSize(metadataSize);
          if (suggestedDeviceType && suggestedDeviceType !== deviceType) {
            setDeviceType(suggestedDeviceType);
          }
        }
      }
      
      if (!silent) showSnackBar('Preview generated successfully', 'success', { event: 'preview_generated', campaign_id: selectedCampaignId, creative_id: selectedCreativeId });
    } catch (err: any) {
      const errorResponse = err.response?.data || err.response;
      if (errorResponse?.diagnostics) {
        setLiveDiagnostics(errorResponse.diagnostics);
        setLiveEligibility(null);
        setError(null);
      } else if (errorResponse?.eligibility) {
        setLiveEligibility(errorResponse.eligibility);
        setError(null);
      } else {
        const payload = errorResponse || {};
        const errorCode = mapEligibilityCode(payload);
        setError(errorCode);
      }
    } finally {
      setIsGenerating(false);
    }
  }, [
    previewMode,
    sourceMode,
    selectedCampaignId,
    selectedCreativeId,
    selectedLocationId,
    rawTag,
    fireTracking,
    buildPreviewRequest,
    apiHost,
    showSnackBar,
    deviceType,
  ]);

  // Auto-generate track whether initial auto-gen has fired
  const hasAutoGeneratedOnLoad = useRef(false);

  useEffect(() => {
    if (hasAutoGeneratedOnLoad.current) return;
    if (!context) return;
    // Only auto-generate on load for simulated mode — live mode requires manual generate
    if (previewMode === 'live') return;

    const canGenerate =
      (sourceMode === 'campaign' && selectedCreativeId) ||
      (sourceMode === 'raw_tag' && rawTag.trim());

    if (!canGenerate) return;

    hasAutoGeneratedOnLoad.current = true;
    handleGeneratePreview({ silent: true });
  }, [context, sourceMode, selectedCreativeId, rawTag, previewMode, handleGeneratePreview]);

  const regenTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isInitialRender = useRef(true);

  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return undefined;
    }

    if (previewMode === 'live' || !autoRefresh) return undefined;

    const canGenerate =
      (sourceMode === 'campaign' && selectedCreativeId) ||
      (sourceMode === 'raw_tag' && rawTag.trim());

    if (!canGenerate) return undefined;

    if (regenTimer.current) clearTimeout(regenTimer.current);
    regenTimer.current = setTimeout(() => {
      handleGeneratePreview({ silent: true });
    }, 600);

    return () => {
      if (regenTimer.current) clearTimeout(regenTimer.current);
    };
  }, [
    previewMode,
    autoRefresh,
    sourceMode,
    selectedCreativeId,
    selectedCampaignId,
    selectedLocationId,
    locationType,
    customLat,
    customLon,
    deviceType,
    deviceOS,
    selectedSize,
    rawTag,
    fireTracking,
    campaignUrls,
    pixelUrls,
    handleGeneratePreview,
  ]);

  const handleLoadContext = useCallback(async (campaignId: number | null, creativeId?: number) => {
    try {
      setContext(null);
      setPreviewResponse(null);
      setError(null);

      const contextData = await fetchPreviewContext(apiHost, {
        ...(campaignId ? { campaign_id: campaignId } : {}),
        ...(creativeId ? { creative_id: creativeId } : {}),
      });
      
      setContext(contextData);

      if (contextData.mode === 'full_context' || !contextData.mode) {
        if (campaignId) {
          setSelectedCampaignId(campaignId);
        }

        if (creativeId) {
          setSelectedCreativeId(creativeId);
          
          if (contextData.context.targeted_creatives && contextData.context.targeted_creatives.length > 0) {
            const matchingCreative = contextData.context.targeted_creatives.find(
              (tc) => tc.creative_id === creativeId
            );
            if (matchingCreative?.creative.size) {
              const creativeSize = matchingCreative.creative.size;
              // Handle "VIDEO_AD" size - always default video to mobile 320x180
              if (creativeSize === 'VIDEO_AD' || (matchingCreative.creative.is_video && !isVideoFriendlySize(creativeSize))) {
                setSelectedSize('320x180');
                setDeviceType('mobile');
              } else {
                const validSize = getValidSizeOrDefault(creativeSize);
                setSelectedSize(validSize);
                const suggestedDeviceType = getDeviceTypeFromAdSize(validSize);
                if (suggestedDeviceType) {
                  setDeviceType(suggestedDeviceType);
                }
              }
            } else if (matchingCreative?.creative.is_video) {
              // Video creative but no size - default to mobile 320x180
              setSelectedSize('320x180');
              setDeviceType('mobile');
            } else {
              // No size detected - use default fallback
              setSelectedSize(DEFAULT_FALLBACK_SIZE);
            }
          } else if (contextData.context.creative?.size) {
            // Fallback to context creative size if available
            const creativeSize = contextData.context.creative.size;
            // Handle "VIDEO_AD" size string - always default video to mobile 320x180
            if (creativeSize === 'VIDEO_AD' || (contextData.context.creative.is_video && !isVideoFriendlySize(creativeSize))) {
              setSelectedSize('320x180');
              setDeviceType('mobile');
            } else {
              const validSize = getValidSizeOrDefault(creativeSize);
              setSelectedSize(validSize);
              const suggestedDeviceType = getDeviceTypeFromAdSize(validSize);
              if (suggestedDeviceType) {
                setDeviceType(suggestedDeviceType);
              }
            }
          } else if (contextData.context.creative?.is_video) {
            // Video creative but no size - default to mobile 320x180
            setSelectedSize('320x180');
            setDeviceType('mobile');
          } else {
            // No size detected for creative - use default fallback
            setSelectedSize(DEFAULT_FALLBACK_SIZE);
          }
        } else if (contextData.context.targeted_creatives && contextData.context.targeted_creatives.length > 0) {
          // No creativeId provided - auto-select first creative from dropdown
          const firstCreative = contextData.context.targeted_creatives[0];
          setSelectedCreativeId(firstCreative.creative_id);
          // Auto-select device type and size based on first creative size
          if (firstCreative.creative.size) {
            const creativeSize = firstCreative.creative.size;
            // Handle "VIDEO_AD" size string - always default video to mobile 320x180
            if (creativeSize === 'VIDEO_AD' || (firstCreative.creative.is_video && !isVideoFriendlySize(creativeSize))) {
              setSelectedSize('320x180');
              setDeviceType('mobile');
            } else {
              const validSize = getValidSizeOrDefault(creativeSize);
              setSelectedSize(validSize);
              const suggestedDeviceType = getDeviceTypeFromAdSize(validSize);
              if (suggestedDeviceType) {
                setDeviceType(suggestedDeviceType);
              }
            }
          } else if (firstCreative.creative.is_video) {
            // Video creative but no size - default to mobile 320x180
            setSelectedSize('320x180');
            setDeviceType('mobile');
          } else {
            // No size detected for first creative - use default fallback
            setSelectedSize(DEFAULT_FALLBACK_SIZE);
          }
        } else if (contextData.context.creative?.size) {
          // Creative-only mode: use creative size from context
          const creativeSize = contextData.context.creative.size;
          // Handle "VIDEO_AD" size string - always default video to mobile 320x180
          if (creativeSize === 'VIDEO_AD' || (contextData.context.creative.is_video && !isVideoFriendlySize(creativeSize))) {
            setSelectedSize('320x180');
            setDeviceType('mobile');
          } else {
            const validSize = getValidSizeOrDefault(creativeSize);
            setSelectedSize(validSize);
            const suggestedDeviceType = getDeviceTypeFromAdSize(validSize);
            if (suggestedDeviceType) {
              setDeviceType(suggestedDeviceType);
            }
          }
        } else if (contextData.context.creative?.is_video) {
          // Video creative but no size - default to mobile 320x180
          setSelectedSize('320x180');
          setDeviceType('mobile');
        } else {
          // No context available to determine size - use default fallback
          setSelectedSize(DEFAULT_FALLBACK_SIZE);
        }

        // Reset location to first available location
        if (contextData.context.locations && contextData.context.locations.length > 0) {
          setSelectedLocationId(contextData.context.locations[0].id);
        } else {
          setSelectedLocationId(null);  // reset when new campaign has no locations
        }
      } else if (contextData.mode === 'creative_only') {
        // Creative-only mode: user can select from available campaigns or use QA mode
        // Don't auto-select campaign, let user choose
        if (creativeId) {
          setSelectedCreativeId(creativeId);
        }
      }
    } catch (err) {
      setContext(null);
      setSelectedCampaignId(null);
      setSelectedCreativeId(null);
      throw err;
    }
  }, [apiHost]);

  /**
   * Handle creative change and auto-select device type based on creative size
   */
  const handleCreativeChange = useCallback((creativeId: number | null) => {
    setSelectedCreativeId(creativeId);

    if (creativeId && context) {
      // Try to find creative in targeted_creatives
      const targetedCreatives = context.context.targeted_creatives || [];
      const matchingCreative = targetedCreatives.find(
        (tc) => tc.creative_id === creativeId
      );

      if (matchingCreative?.creative.size) {
        const creativeSize = matchingCreative.creative.size;
        // Handle "VIDEO_AD" size string - convert to proper video size
        // Always default video creatives to mobile device with 320x180 size
        if (creativeSize === 'VIDEO_AD' || (matchingCreative.creative.is_video && !isVideoFriendlySize(creativeSize))) {
          setSelectedSize('320x180');
          setDeviceType('mobile');
        } else {
          const validSize = getValidSizeOrDefault(creativeSize);
          setSelectedSize(validSize);
          const suggestedDeviceType = getDeviceTypeFromAdSize(validSize);
          if (suggestedDeviceType) {
            setDeviceType(suggestedDeviceType);
          }
        }
      } else if (matchingCreative?.creative.is_video) {
        // Video creative but no size - default to mobile 320x180
        setSelectedSize('320x180');
        setDeviceType('mobile');
      } else if (matchingCreative) {
        // Matching creative found but no valid size - use default fallback
        setSelectedSize(DEFAULT_FALLBACK_SIZE);
      } else if (context.context.creative?.id === creativeId && context.context.creative.size) {
        // Fallback to context creative if available
        const creativeSize = context.context.creative.size;
        // Handle "VIDEO_AD" size string - convert to proper video size
        // Always default video creatives to mobile device with 320x180 size
        if (creativeSize === 'VIDEO_AD' || (context.context.creative.is_video && !isVideoFriendlySize(creativeSize))) {
          setSelectedSize('320x180');
          setDeviceType('mobile');
        } else {
          const validSize = getValidSizeOrDefault(creativeSize);
          setSelectedSize(validSize);
          const suggestedDeviceType = getDeviceTypeFromAdSize(validSize);
          if (suggestedDeviceType) {
            setDeviceType(suggestedDeviceType);
          }
        }
      } else if (context.context.creative?.id === creativeId && context.context.creative.is_video) {
        // Video creative but no size - default to mobile 320x180
        setSelectedSize('320x180');
        setDeviceType('mobile');
      } else {
        // No matching creative found or no size info available - use default fallback
        setSelectedSize(DEFAULT_FALLBACK_SIZE);
      }
    }
  }, [context]);

  /**
   * Get breadcrumb data from context
   */
  const getBreadcrumb = () => {
    if (!context) return null;

    // Handle creative_only mode explicitly
    if (context.mode === 'creative_only') {
      const {creative} = context.context;
      return creative
        ? {
            client: undefined,
            io: undefined,
            campaign: 'QA Mode',
            creative: creative.name,
          }
        : null;
    }

    // Handle full_context mode with defensive optional chaining
    const { client, insertion_order: insertionOrder, campaign, creative } = context.context;
    return {
      client: client?.name,
      io: insertionOrder?.name,
      campaign: campaign?.name,
      creative: creative?.name,
    };
  };

  /**
   * Get status pills data
   */
  const getStatusPills = (): Array<{
    label: string;
    color: 'success' | 'error' | 'warning' | 'info' | 'default';
  }> => {
    if (!previewResponse) return [];

    const pills: Array<{
      label: string;
      color: 'success' | 'error' | 'warning' | 'info' | 'default';
    }> = [
      { label: 'Simulated', color: 'success' },
    ];

    if (previewResponse.metadata.vendor) {
      pills.push({
        label: previewResponse.metadata.vendor.toUpperCase(),
        color: 'default',
      });
    }

    pills.push({
      label: `${previewResponse.metadata.creative_type.toUpperCase()} ${previewResponse.metadata.size}`,
      color: 'info',
    });

    return pills;
  };

  // Calculate effective QA mode based on actual backend behavior
  // (not just UI state - backend may run in QA mode even with a campaign selected)
  const locations = context?.context.locations ?? [];
  const hasCampaign = !!selectedCampaignId;
  const hasLocations = locations.length > 0;

  // Whether the backend is actually running in QA-ish mode
  const isEffectiveQAMode =
    sourceMode === 'raw_tag' ||
    (!selectedCampaignId && !!selectedCreativeId) || // pure creative-only
    (sourceMode === 'campaign' && hasCampaign && !hasLocations) || // campaign with no locations → QA under hood
    (sourceMode === 'campaign' && locationType === 'custom'); // custom coords → QA under hood

  // QR code data for mobile preview (only available when IO, campaign, and creative are selected)
  const qrCodeData = useMemo(() => {
    const insertionOrderId = context?.context.insertion_order?.id;
    const creativeName = context?.context.creative?.name ||
      context?.context.targeted_creatives?.find(tc => tc.creative_id === selectedCreativeId)?.creative.name;

    if (!insertionOrderId || !selectedCampaignId || !selectedCreativeId) {
      return null;
    }

    return {
      insertionOrderId,
      campaignId: selectedCampaignId,
      creativeId: selectedCreativeId,
      creativeName: creativeName || 'Creative Preview',
    };
  }, [context, selectedCampaignId, selectedCreativeId]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
      {/* Top Bar */}
      <TopBar
        breadcrumb={getBreadcrumb()}
        statusPills={getStatusPills()}
        mode={previewMode}
        onModeChange={handleModeChange}
        onGenerate={handleGeneratePreview}
        isGenerating={isGenerating}
        lastGeneratedTime={lastGeneratedTime}
        autoRefresh={autoRefresh}
        onAutoRefreshChange={setAutoRefresh}
        disabled={
          (sourceMode === 'campaign' && !selectedCreativeId && !isCreativeOnly) ||
          (sourceMode === 'raw_tag' && !rawTag.trim()) ||
          (previewMode === 'live' && (!selectedCampaignId || !selectedCreativeId))
        }
        isQAMode={isEffectiveQAMode}
        liveEligibility={liveEligibility}
        checkingEligibility={checkingEligibility}
        onRefreshEligibility={handleCheckEligibility}
        sourceMode={sourceMode}
      />

      {/* Main Content: Three-Panel Layout */}
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Left Panel: Controls */}
        <Box
          sx={{
            width: 320,
            borderRight: 1,
            borderColor: 'divider',
            overflow: 'auto',
            bgcolor: 'background.paper',
          }}
        >
          <ControlsPanel
            context={context}
            sourceMode={sourceMode}
            onSourceModeChange={handleSourceModeChange}
            selectedCampaignId={selectedCampaignId}
            onCampaignChange={setSelectedCampaignId}
            selectedCreativeId={selectedCreativeId}
            onCreativeChange={handleCreativeChange}
            locationType={locationType}
            onLocationTypeChange={setLocationType}
            selectedLocationId={selectedLocationId}
            onLocationChange={setSelectedLocationId}
            customLat={customLat}
            onCustomLatChange={setCustomLat}
            customLon={customLon}
            onCustomLonChange={setCustomLon}
            deviceType={deviceType}
            onDeviceTypeChange={setDeviceType}
            deviceOS={deviceOS}
            onDeviceOSChange={setDeviceOS}
            selectedSize={selectedSize}
            onSizeChange={setSelectedSize}
            expandableMode={expandableMode}
            onExpandableModeChange={setExpandableMode}
            rawTag={rawTag}
            onRawTagChange={setRawTag}
            campaignUrls={campaignUrls}
            onCampaignUrlsChange={setCampaignUrls}
            campaignUrlInput={campaignUrlInput}
            onCampaignUrlInputChange={setCampaignUrlInput}
            pixelUrls={pixelUrls}
            onPixelUrlsChange={setPixelUrls}
            pixelUrlInput={pixelUrlInput}
            onPixelUrlInputChange={setPixelUrlInput}
            onLoadContext={handleLoadContext}
            isCreativeOnlyMode={isCreativeOnly}
            previewMode={previewMode}
            liveEligibility={liveEligibility}
            fireTracking={fireTracking}
            onFireTrackingChange={setFireTracking}
            uploadedImageUrl={uploadedImageUrl}
            onUploadedImageChange={setUploadedImageUrl}
            generateClickTag={generateClickTag}
            onGenerateClickTagChange={setGenerateClickTag}
          />
        </Box>

        {/* Center Panel: Preview Display */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            bgcolor: 'grey.100',
          }}
        >
          <PreviewPanel
            previewResponse={previewResponse}
            isGenerating={isGenerating}
            error={error}
            deviceType={deviceType}
            selectedSize={selectedSize}
            iframeRef={iframeRef}
            expandableMode={expandableMode}
            onShare={handleShareClick}
            onCreateGallery={selectedCampaignId ? handleGalleryClick : undefined}
            qrCodeData={qrCodeData}
          />
        </Box>

        {/* Right Panel: Debug Console */}
        <Box
          sx={{
            width: 360,
            borderLeft: 1,
            borderColor: 'divider',
            overflow: 'auto',
            bgcolor: 'background.paper',
          }}
        >
          <DebugPanel
            previewResponse={previewResponse}
            previewMode={previewMode}
            liveEligibility={liveEligibility}
            liveDiagnostics={liveDiagnostics}
            checkingEligibility={checkingEligibility}
            eligibilityError={eligibilityError}
            onRefreshEligibility={handleCheckEligibility}
            onSwitchToSimulated={() => {
              handleModeChange('simulated');
            }}
            onRetry={handleGeneratePreview}
            fireTracking={fireTracking}
            iframeRef={iframeRef}
          />
        </Box>
      </Box>

      {/* Share with Client Modal */}
      <ShareWithClientModal
        open={shareModalOpen}
        onClose={() => {
          setShareModalOpen(false);
          setShareUrl(null);
          setShareError(null);
        }}
        shareUrl={shareUrl}
        isLoading={isSharing}
        error={shareError}
        onGenerateShare={handleGenerateShare}
        showNameInput={previewMode !== 'live'}
      />

      {/* Gallery Creation Modal */}
      <GalleryCreationModal
        open={galleryModalOpen}
        onClose={() => setGalleryModalOpen(false)}
        creatives={(context?.context.targeted_creatives || []).map((tc: any) => ({
          id: tc.creative?.id ?? tc.creative_id,
          name: tc.creative?.name ?? `Creative ${tc.creative?.id ?? tc.creative_id}`,
          size: tc.creative?.size,
          link: tc.creative?.link,
          has_tag: tc.creative?.ad_type !== 'hosted' && tc.creative?.ad_type !== 2,
        }))}
        campaignId={selectedCampaignId}
        onCreateGallery={handleCreateGallery}
      />
    </Box>
  );
}
